import {
  DomainErrorCodeSchema,
  createCorrelationId,
  type DomainErrorCode,
} from "@repo/domain-events";
import { getRouteVisitId } from "~/lib/route-visit";
import {
  startSentryUserActionObservation,
  type SentryUserActionObservation,
} from "~/lib/sentry";
import { trackTelemetryEvent } from "~/lib/telemetry";
import type { UserActionContext } from "~/lib/user-actions";

export interface UserActionFailureOptions {
  readonly captureException?: boolean;
  readonly correlationId?: string | null;
  readonly errorCode?: DomainErrorCode | null;
  readonly extra?: Record<string, unknown>;
}

export interface UserActionObservation {
  readonly fail: (
    error: unknown,
    options?: UserActionFailureOptions,
  ) => string | null;
  readonly interactionId: string;
  /**
   * Runs request creation with the Sentry action span active. The callback may
   * return a Promise; browser fetch instrumentation reads the active span when
   * that request is created.
   */
  readonly run: <T>(callback: () => T) => T;
  readonly succeed: (extra?: Record<string, unknown>) => void;
}

export interface StartUserActionOptions {
  /**
   * Reuse a durable domain correlation that was already minted alongside an
   * idempotency key.
   */
  readonly correlationId?: string | null;
}

function getUserActionErrorCode(context: UserActionContext): DomainErrorCode {
  return DomainErrorCodeSchema.parse(
    `${context.action.replaceAll(".", "_")}_failed`,
  );
}

function getDurationMs(startedAt: number): number {
  return Math.max(0, Math.round(performance.now() - startedAt));
}

export function startUserAction(
  context: UserActionContext,
  options: StartUserActionOptions = {},
): UserActionObservation {
  const interactionId =
    options.correlationId?.trim() || createCorrelationId();
  const startedAt = performance.now();
  const sentryObservation: SentryUserActionObservation =
    startSentryUserActionObservation(context, interactionId);
  let finished = false;

  trackTelemetryEvent("frontend.user_action.started", {
    action: context.action,
    route_visit_id: getRouteVisitId(),
    surface: context.surface,
  });

  return {
    fail(error, options = {}) {
      if (finished) {
        return null;
      }
      finished = true;
      const errorCode = options.errorCode ?? getUserActionErrorCode(context);
      const sentryEventId = sentryObservation.fail(error, {
        ...options,
        errorCode,
      });

      trackTelemetryEvent("frontend.user_action.failed", {
        action: context.action,
        duration_ms: getDurationMs(startedAt),
        error_code: errorCode,
        route_visit_id: getRouteVisitId(),
        surface: context.surface,
      });
      return sentryEventId;
    },
    interactionId,
    run: (callback) => sentryObservation.run(callback),
    succeed(extra) {
      if (finished) {
        return;
      }
      finished = true;
      sentryObservation.succeed(extra);
      trackTelemetryEvent("frontend.user_action.succeeded", {
        action: context.action,
        duration_ms: getDurationMs(startedAt),
        route_visit_id: getRouteVisitId(),
        surface: context.surface,
      });
    },
  };
}
